/**
 * finopsDashboard.ts — Cliente SP3 para os endpoints de dashboard interativo FinOps.
 *
 * Endpoints cobertos:
 *   GET  /api/v1/finops/my-portfolio
 *   GET  /api/v1/finops/cost-breakdown
 *   GET  /api/v1/tenants/{id}/finops/cost-breakdown
 *   GET  /api/v1/finops/trend
 *   GET  /api/v1/tenants/{id}/finops/trend
 *   POST /api/v1/finops/refresh
 *
 * Usa o mesmo mecanismo HTTP (`api` axios instance de `./client`) que `finops.ts`.
 * mom_pct / yoy_pct podem ser null (D4: exibe "—" quando null).
 */
import { api } from './client'

// ─────────────────────────────────────────────
// Tipos de parâmetro
// ─────────────────────────────────────────────

export interface FinopsPeriod {
  start: string       // YYYY-MM-DD
  end: string         // YYYY-MM-DD
  granularity?: 'DAILY' | 'MONTHLY'
}

export type FinopsGroupBy = 'account' | 'service'

export interface BreakdownParams {
  groupBy: FinopsGroupBy
  accountId?: string
  service?: string
}

// ─────────────────────────────────────────────
// Shapes de resposta (§8 da spec)
// ─────────────────────────────────────────────

export type ScopeReason =
  | 'admin_global'
  | 'scoped'
  | 'tenant_without_account'
  | 'no_access'

export interface PortfolioTopSpender {
  tenant_id: number
  name: string
  amount_usd: number
}

export interface PortfolioKpis {
  total_usd: number
  mom_pct: number | null
  yoy_pct: number | null
  budget_total_usd: number
  /** Custo consolidado apenas dos tenants que possuem orçamento cadastrado. */
  budgeted_cost_usd: number
  /** Quantidade de tenants sem orçamento cadastrado no período. */
  tenants_without_budget: number
  tenants_over_budget: number
  savings_potential_usd: number
  top_spender_tenant: PortfolioTopSpender | null
}

export interface PortfolioTenantRow {
  tenant_id: number
  name: string
  account_id: string
  amount_usd: number
  /** Custo do mês anterior (pode ser 0 para tenant novo). */
  prev_amount_usd: number
  /** Delta real em USD: amount_usd − prev_amount_usd (backend calculado, pode ser negativo). */
  delta_usd: number
  budget_usd: number
  pct_used: number
  status: string          // 'verde' | 'amarelo' | 'vermelho'
  /** null quando prev_amount_usd < US$0,01 (tenant novo — sem base para calcular %). */
  mom_pct: number | null
  anomaly: boolean
}

export interface UnmappedAccount {
  account_id: string
  amount_usd: number
}

export interface PortfolioResponse {
  scope_reason: ScopeReason
  cost_available: boolean
  error_code: string | null
  as_of: string            // ISO date string
  is_partial: boolean
  kpis: PortfolioKpis
  tenants: PortfolioTenantRow[]
  unmapped_accounts: UnmappedAccount[]
}

export interface BreakdownItem {
  key: string
  amount_usd: number
  pct: number
}

export interface BreakdownResponse {
  group_by: FinopsGroupBy
  items: BreakdownItem[]
  as_of: string
  is_partial: boolean
  cost_available: boolean
  error_code?: string | null
  scope_reason?: ScopeReason | null
}

export interface TrendPoint {
  period_start: string    // ISO date string
  amount_usd: number
  is_partial: boolean
  as_of?: string | null
}

export interface TrendResponse {
  points: TrendPoint[]
  mom_pct: number | null
  yoy_pct: number | null
  as_of: string
  is_partial: boolean
  cost_available: boolean
  error_code?: string | null
}

export interface RefreshResponse {
  refreshed_at: string    // ISO datetime string
  scope_reason: ScopeReason
}

// ─────────────────────────────────────────────
// Funções de cliente
// ─────────────────────────────────────────────

/**
 * Portfólio agregado da plataforma (admin/gestor).
 * Backend: GET /api/v1/finops/my-portfolio?start&end
 */
export function getPortfolio(period: FinopsPeriod): Promise<PortfolioResponse> {
  return api
    .get<PortfolioResponse>('/v1/finops/my-portfolio', {
      params: { start: period.start, end: period.end },
    })
    .then((r) => r.data)
}

/**
 * Breakdown de custo no escopo da plataforma (por conta ou serviço).
 * Backend: GET /api/v1/finops/cost-breakdown?start&end&group_by=account|service&account_id=&service=
 */
export function getCostBreakdown(
  period: FinopsPeriod,
  { groupBy, accountId, service }: BreakdownParams,
): Promise<BreakdownResponse> {
  return api
    .get<BreakdownResponse>('/v1/finops/cost-breakdown', {
      params: {
        start: period.start,
        end: period.end,
        group_by: groupBy,
        ...(accountId ? { account_id: accountId } : {}),
        ...(service ? { service } : {}),
      },
    })
    .then((r) => r.data)
}

/**
 * Breakdown de custo por tenant específico (drill-down).
 * Backend: GET /api/v1/tenants/{id}/finops/cost-breakdown?start&end&group_by=&service=
 */
export function getTenantCostBreakdown(
  tenantId: number,
  period: FinopsPeriod,
  { groupBy, accountId, service }: BreakdownParams,
): Promise<BreakdownResponse> {
  return api
    .get<BreakdownResponse>(`/v1/tenants/${tenantId}/finops/cost-breakdown`, {
      params: {
        start: period.start,
        end: period.end,
        group_by: groupBy,
        ...(accountId ? { account_id: accountId } : {}),
        ...(service ? { service } : {}),
      },
    })
    .then((r) => r.data)
}

/**
 * Tendência de custo no escopo da plataforma.
 * Backend: GET /api/v1/finops/trend?start&end&granularity=DAILY|MONTHLY
 */
export function getTrend(
  period: FinopsPeriod,
  granularity: 'DAILY' | 'MONTHLY' = 'MONTHLY',
): Promise<TrendResponse> {
  return api
    .get<TrendResponse>('/v1/finops/trend', {
      params: {
        start: period.start,
        end: period.end,
        granularity,
      },
    })
    .then((r) => r.data)
}

/**
 * Tendência de custo por tenant específico.
 * Backend: GET /api/v1/tenants/{id}/finops/trend?start&end&granularity=DAILY|MONTHLY
 */
export function getTenantTrend(
  tenantId: number,
  period: FinopsPeriod,
  granularity: 'DAILY' | 'MONTHLY' = 'MONTHLY',
): Promise<TrendResponse> {
  return api
    .get<TrendResponse>(`/v1/tenants/${tenantId}/finops/trend`, {
      params: {
        start: period.start,
        end: period.end,
        granularity,
      },
    })
    .then((r) => r.data)
}

/**
 * Dispara refresh manual do escopo FinOps.
 * Requer permissão `finops:refresh` (admin bypass automático).
 * Backend: POST /api/v1/finops/refresh  body: { scope_hint? }
 */
export function postRefresh(scopeHint?: string): Promise<RefreshResponse> {
  return api
    .post<RefreshResponse>('/v1/finops/refresh', {
      ...(scopeHint ? { scope_hint: scopeHint } : {}),
    })
    .then((r) => r.data)
}
